// frontend/src/features/ups/UpsDriverStats.js
// This React component renders UPS driver stats & controls—source selector, agency selector, 
// search box, a toggle for global search, and counters that reflect the current filters.
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectAllAgencies, fetchAgencies } from '../agencies/agenciesSlice';
import {
  selectFilters,
  selectSource,
  setAgencyId,
  setQuery,
  setScope,
  setSource,
  selectStatsRefreshKey,
} from '../ui/filtersSlice';

import StatusCounters from '../../components/StatusCounters';
import SiteSelector from '../../components/SiteSelector';

import { selectConcreteSelectedSiteId, selectIsAllSitesMode, } from '../sites/sitesSlice';
import { fetchSiteAssignments, fetchCompanyAssignments, } from '../../services/assignmentService';
import { fetchBannedDrivers } from '../../services/upsDriverService';

export default function UpsDriverStats() {
  const dispatch = useDispatch();
  const sourceSelectId = 'ups-driver-stats-source';
  const agencySelectId = 'ups-driver-stats-agency';
  const searchInputId = 'ups-driver-stats-search';
  const scopeToggleId = 'ups-driver-stats-scope';
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q, scope } = useSelector(selectFilters);
  const source = useSelector(selectSource);
  const siteId = useSelector(selectConcreteSelectedSiteId);
  const isAllSitesMode = useSelector(selectIsAllSitesMode);
  const statsRefreshKey = useSelector(selectStatsRefreshKey);

  // Source filter change handler
  const onSourceChange = (e) => {
    const nextSource = e.target.value;
    dispatch(setSource(nextSource));

    // Clear stale agency filter if switching to internal
    if (nextSource === 'internal') {
      dispatch(setAgencyId(''));
    }
  };

  // StatusCounters expects keys like Active/Reserved/Banned (matches your UI routes)
  const [counts, setCounts] = useState({ Total: 0, Active: 0, Reserved: 0, Banned: 0 });

  // Ensure agencies are loaded for the dropdown
  useEffect(() => {
    if (!agencies?.length) dispatch(fetchAgencies());
  }, [dispatch, agencies?.length]);

  // Compute counts from correct sources of truth
  useEffect(() => {
    if (!isAllSitesMode && siteId == null) {
      setCounts({ Total: 0, Active: 0, Reserved: 0, Banned: 0 });
      return;
    }

    let ignore = false;
    (async () => {
      try {
        // Defensively ignore stale agency state if source is internal
        const agencyIdNum = source === 'internal' || !agencyId ? undefined : Number(agencyId);
        const search = q?.trim() ? q.trim() : undefined;

        // In tab search mode, the current lane owns searched fetching.
        // Prevent stats from doing hidden cross-lane searched requests.
        if (scope === 'tab' && search) {
          setCounts({ Total: 0, Active: 0, Reserved: 0, Banned: 0 });
          return;
        }

        // NOTE: scope toggle ("global" vs "tab")
        // - "tab": counts should match current site context
        // - "global": site-scoped for Active/Reserved because assignments are site lists.
        const assignmentFetcher = isAllSitesMode
          ? fetchCompanyAssignments
          : (options) => fetchSiteAssignments(siteId, options);

        const [activeRows, reservedRows, bannedRows] = await Promise.all([
          assignmentFetcher({
            statuses: ['active'],
            agencyId: agencyIdNum,
            search,
            source,
          }),
          assignmentFetcher({
            statuses: ['confirmed'],
            agencyId: agencyIdNum,
            search,
            source,
          }),
          fetchBannedDrivers({
            agencyId: agencyIdNum,
            search,
            source,
          }),
        ]);

        if (!ignore) {
          const activeList = Array.isArray(activeRows) ? activeRows : [];
          const reservedList = Array.isArray(reservedRows) ? reservedRows : [];
          const bannedList = Array.isArray(bannedRows) ? bannedRows : [];

          // Do not count banned drivers inside operational buckets
          const Active = activeList.filter((r) => !r?.is_banned).length;
          const Reserved = reservedList.filter((r) => !r?.is_banned).length;
          const Banned = bannedList.length;

          setCounts({
            Total: Active + Reserved + Banned,
            Active,
            Reserved,
            Banned,
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[UpsDriverStats] count fetch failed', err);
        if (!ignore) setCounts({ Total: 0, Active: 0, Reserved: 0, Banned: 0 });
      }
    })();

    return () => {
      ignore = true;
    };
    }, [siteId, isAllSitesMode, agencyId, q, scope, source, statsRefreshKey]);

  const onAgencyChange = (e) => dispatch(setAgencyId(e.target.value)); 
  const onSearchChange = (e) => dispatch(setQuery(e.target.value));
  const onScopeToggle = (e) => dispatch(setScope(e.target.checked ? 'global' : 'tab'));

  const agenciesSorted = useMemo(() => {
    const list = Array.isArray(agencies) ? agencies : [];
    return [...list].sort((a, b) =>
      (a?.name ?? a?.agency_name ?? '').localeCompare(b?.name ?? b?.agency_name ?? '')
    );
  }, [agencies]);

  const getAgencyIdStr = (a) => String(a?.id ?? a?.agency_id ?? a?.agencyId ?? '');
  const labelClass = 'mb-2 block text-xs font-semibold uppercase tracking-[0.22em]';
  const controlClass =
    'w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-accent/30';

    return (
    <section className="mb-6 space-y-5 text-app-text">

      {/* Controls */}
      <div className="rounded-2xl border border-app-border bg-app-card p-4 shadow-sm sm:p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-12 xl:items-end">
          <div className="min-w-0 xl:col-span-3">
            <SiteSelector />
          </div>

          {/* Source dropdown */}
          <div className="min-w-0 xl:col-span-2">
            <label
              htmlFor={sourceSelectId}
              className={`${labelClass} text-app-muted`}
            >
              Source
            </label>
            <select
              id={sourceSelectId}
              name="source"
              value={source}
              onChange={onSourceChange}
              className={controlClass}
            >
              <option value="all">All Sources</option>
              <option value="agency">Agency</option>
              <option value="internal">Internal</option>
            </select>
          </div>

          {/* Agency dropdown */}
          <div className="min-w-0 xl:col-span-2">
            <label
              htmlFor={agencySelectId}
              className={`${labelClass} ${
                source === 'internal' ? 'text-app-muted/50' : 'text-app-muted'
              }`}
            >
              Agency
            </label>
            <select
              id={agencySelectId}
              name="agencyId"
              value={agencyId === '' ? '' : String(agencyId)}
              onChange={onAgencyChange}
              disabled={source === 'internal'}
              className={`w-full rounded-xl border border-app-border px-4 py-3 text-sm outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-accent/30 ${
                source === 'internal'
                  ? 'cursor-not-allowed bg-app-surface/60 text-app-muted/50'
                  : 'bg-app-card text-app-text'
              }`}
            >
              <option value="">All Agencies</option>
              {agenciesSorted.map((a) => {
                const idStr = getAgencyIdStr(a);
                return (
                  <option key={idStr || (a?.name ?? a?.agency_name) || 'unknown'} value={idStr}>
                    {a?.name ?? a?.agency_name ?? '(Unnamed)'}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Search */}
          <div className="min-w-0 md:col-span-2 xl:col-span-3">
            <label
              htmlFor={searchInputId}
              className={`${labelClass} text-app-muted`}
            >
              Search
            </label>
            <input
              id={searchInputId}
              name="driverSearch"
              value={q}
              onChange={onSearchChange}
              placeholder="Search driver, reg, phone, or email"
              className={`${controlClass} placeholder:text-app-muted/70`}
            />
          </div>

          {/* Scope toggle */}
          <label
            htmlFor={scopeToggleId}
            className="inline-flex w-full min-w-0 items-center justify-center gap-2 rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm font-medium text-app-text md:col-span-2 xl:col-span-2"
          >
            <input
              id={scopeToggleId}
              name="searchAllStatuses"
              type="checkbox"
              checked={scope === 'global'}
              onChange={onScopeToggle}
              className="h-4 w-4 rounded border-app-border"
            />
            <span className="whitespace-normal text-center sm:whitespace-nowrap">Search all statuses</span>
          </label>
        </div>
      </div>

      {/* Figures */}
      <StatusCounters title={null} counts={counts} />
      </section>
  );
}
